import { ref, computed } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { deepCopy } from "@utils/toolBox";
import handleError from "@utils/composables/handleError";
import { useMCampaignsStore } from "@stores/mCampaigns";
import { useToastsStore } from "@/stores/toastsStore";
import { captureEvent } from "@/lib/analytics";

export const useCCampaignsStore = defineStore("cCampaigns", () => {
  const cCampaigns = ref([]);
  const initialCCampaigns = ref([]);

  const cCampaignOptions = computed(() => cCampaigns.value.map((cc) => ({ label: cc.name, value: cc.id })));

  const hasCCampaignsChanged = computed(
    () => JSON.stringify(initialCCampaigns.value) !== JSON.stringify(cCampaigns.value),
  );

  async function fetchCCampaigns(mCampaignToken) {
    try {
      const { response, data } = await useMyFetch(`/m_campaigns/${mCampaignToken}/c_campaigns`).json();
      if (response.value.ok) {
        cCampaigns.value = data.value;
        initialCCampaigns.value = deepCopy(data.value);
      }
      return cCampaigns.value;
    } catch (err) {
      handleError(err);
    }
  }

  const clearCCampaigns = () => {
    cCampaigns.value = [];
    initialCCampaigns.value = [];
  };

  const setCCampaignBudgetManual = (cCampaignId, value) => {
    const cCampaign = cCampaigns.value.find((cc) => cc.id === cCampaignId);
    if (cCampaign) cCampaign.daily_budget_manual = value;
  };

  function syncMCampaignsList(mCampaignToken, cCampaignGroupToken, updatedCCampaign) {
    const mCampaignsStore = useMCampaignsStore();
    if (!Array.isArray(mCampaignsStore.mCampaigns)) return;

    const mCampaign = mCampaignsStore.mCampaigns.find((mc) => mc.token === mCampaignToken);
    if (mCampaign?.c_campaigns) {
      const ccIndex = mCampaign.c_campaigns.findIndex((cc) => cc.group_token === cCampaignGroupToken);
      if (ccIndex !== -1) mCampaign.c_campaigns[ccIndex] = updatedCCampaign;
    }

    const status = cCampaigns.value.some((cc) => cc.status === "active") ? "active" : "inactive";
    mCampaignsStore.syncMCampaignStatus(updatedCCampaign.m_campaign_id, status);
  }

  function toggleCCampaign(mCampaignToken, cCampaignGroupToken) {
    const index = cCampaigns.value.findIndex((cc) => cc.group_token === cCampaignGroupToken);
    if (index === -1) return;

    const current = cCampaigns.value[index];
    const updated = { ...current, status: current.status === "active" ? "inactive" : "active" };
    cCampaigns.value[index] = updated;

    syncMCampaignsList(mCampaignToken, cCampaignGroupToken, updated);
  }

  async function toggleCCampaignImmediate(mCampaignToken, cCampaignGroupToken) {
    toggleCCampaign(mCampaignToken, cCampaignGroupToken);

    try {
      const { response, error } = await useMyFetch(
        `/m_campaigns/${mCampaignToken}/c_campaigns/${cCampaignGroupToken}/toggle`,
      )
        .patch()
        .json();
      if (response.value.ok) {
        captureEvent("c_campaign_toggled", { m_campaign_token: mCampaignToken });
      } else {
        toggleCCampaign(mCampaignToken, cCampaignGroupToken);
        if (typeof error.value === "string") useToastsStore().error(error.value);
      }
    } catch (err) {
      toggleCCampaign(mCampaignToken, cCampaignGroupToken);
      handleError(err);
    }
  }

  async function saveCCampaignsBudgets(mCampaignToken) {
    try {
      if (hasCCampaignsChanged.value === false) return Promise.resolve(true);
      const { data, response } = await useMyFetch(`/m_campaigns/${mCampaignToken}/c_campaigns/save_budgets`)
        .put({ c_campaigns: cCampaigns.value })
        .json();
      if (response.value.ok) {
        cCampaigns.value = data.value.c_campaigns;
        initialCCampaigns.value = deepCopy(data.value.c_campaigns);

        captureEvent("c_campaigns_budgets_saved", { m_campaign_token: mCampaignToken });
      }
    } catch (err) {
      handleError(err);
    }
  }

  return {
    cCampaigns,
    cCampaignOptions,
    hasCCampaignsChanged,
    fetchCCampaigns,
    clearCCampaigns,
    setCCampaignBudgetManual,
    toggleCCampaign,
    toggleCCampaignImmediate,
    saveCCampaignsBudgets,
  };
});
